import React, { useState } from 'react';
import { CheckCircle, Mail, ArrowRight, Compass } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

// 60-Second Business Security Health Check
// Light-theme surface so it sits comfortably on the light homepage section.

const questions = [
  {
    id: 'mfa',
    text: 'Do you use Multi-Factor Authentication (MFA) on email, remote access and critical systems?',
    area: 'Identity & Access Security',
    options: [
      { value: 'yes', label: 'Yes, on all critical systems', weight: 3 },
      { value: 'partial', label: 'Partially — some systems only', weight: 2 },
      { value: 'no', label: 'No / not sure', weight: 1 },
    ],
  },
  {
    id: 'access',
    text: 'When an employee leaves, is their access reliably removed within a short, predictable timeframe?',
    area: 'Identity & Access Security',
    options: [
      { value: 'yes', label: 'Yes — we have a documented process', weight: 3 },
      { value: 'partial', label: 'Sometimes — it depends on the person', weight: 2 },
      { value: 'no', label: 'No / not sure', weight: 1 },
    ],
  },
  {
    id: 'vuln',
    text: 'Are your systems, applications and devices regularly assessed for vulnerabilities and updated?',
    area: 'Vulnerability Management',
    options: [
      { value: 'yes', label: 'Yes — on a regular schedule', weight: 3 },
      { value: 'partial', label: 'Occasionally, not on a schedule', weight: 2 },
      { value: 'no', label: 'Rarely or never', weight: 1 },
    ],
  },
  {
    id: 'backup',
    text: 'Do you have backups of critical data — and have you actually tested restoring from them?',
    area: 'Data Protection & Backup',
    options: [
      { value: 'yes', label: 'Yes — backups exist and have been tested', weight: 3 },
      { value: 'partial', label: 'Backups exist but have not been tested', weight: 2 },
      { value: 'no', label: 'No / not sure', weight: 1 },
    ],
  },
  {
    id: 'awareness',
    text: 'Are your employees given regular security awareness training on phishing and social engineering?',
    area: 'Security Awareness & Training',
    options: [
      { value: 'yes', label: 'Yes — regularly and practically', weight: 3 },
      { value: 'partial', label: 'Occasionally, informally', weight: 2 },
      { value: 'no', label: 'No / not sure', weight: 1 },
    ],
  },
];

const summarize = (answers) => {
  const strengths = [];
  const review = [];
  questions.forEach((q) => {
    const a = answers[q.id];
    if (!a) return;
    const opt = q.options.find((o) => o.value === a);
    if (!opt) return;
    if (opt.weight === 3) strengths.push(q.area);
    else review.push(q.area);
  });
  const nextSteps = Array.from(new Set(review)).slice(0, 4);
  const potentialStrengths = Array.from(new Set(strengths));
  return { potentialStrengths, areasToReview: Array.from(new Set(review)), nextSteps };
};

const inputClass = "h-12 bg-white border-slate-300 text-[#101828] placeholder:text-slate-400 focus-visible:ring-blue-500/30 focus-visible:border-blue-500";

const SecurityHealthCheck = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showCapture, setShowCapture] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', companyName: '', companySize: '' });

  const handleAnswer = (qid, value) => {
    const next = { ...answers, [qid]: value };
    setAnswers(next);
    if (step < questions.length - 1) {
      setTimeout(() => setStep(step + 1), 200);
    } else {
      setTimeout(() => setShowCapture(true), 200);
    }
  };

  const totalScore = Object.values(answers).reduce((sum, val) => {
    for (const q of questions) {
      const opt = q.options.find((o) => o.value === val);
      if (opt && q.options.includes(opt)) return sum + opt.weight;
    }
    return sum;
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch('/api/quiz/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          company_name: form.companyName,
          company_size: form.companySize,
          score: totalScore,
          answers,
        }),
      });
      if (!res.ok) throw new Error('Submission failed');
      toast.success('Thank you! Your security snapshot is on its way to your inbox.', {
        icon: <CheckCircle className="text-emerald-500" />,
      });
      setShowResults(true);
      setShowCapture(false);
    } catch (err) {
      toast.error('Something went wrong. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setShowCapture(false);
    setShowResults(false);
    setForm({ name: '', email: '', companyName: '', companySize: '' });
  };

  // Results view
  if (showResults) {
    const { potentialStrengths, areasToReview, nextSteps } = summarize(answers);
    return (
      <div className="max-w-3xl mx-auto" data-testid="health-check-results">
        <div className="relative bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-sm">
          <div className="text-center mb-8">
            <div className="inline-block p-4 bg-blue-50 border border-blue-100 rounded-full mb-4">
              <Compass className="w-10 h-10 text-blue-600" />
            </div>
            <h3 className="font-editorial text-2xl sm:text-3xl font-semibold text-[#06101F] mb-2">Your Security Snapshot</h3>
            <p className="text-[#475467] text-sm sm:text-base max-w-xl mx-auto">
              This is a short snapshot &mdash; not a complete security assessment. Use it as a starting point for a conversation about your business.
            </p>
          </div>

          <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 mb-6">
            <div className="bg-emerald-50/60 border border-emerald-200 rounded-2xl p-5">
              <p className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold mb-3">Potential Strengths</p>
              {potentialStrengths.length ? (
                <ul className="space-y-2">
                  {potentialStrengths.map((s) => (
                    <li key={s} className="flex items-start gap-2 text-[#101828] text-sm">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#475467]">Every business has room to build a stronger baseline &mdash; that&apos;s what we&apos;re here for.</p>
              )}
            </div>
            <div className="bg-amber-50/60 border border-amber-200 rounded-2xl p-5">
              <p className="text-[11px] uppercase tracking-[0.22em] text-amber-700 font-semibold mb-3">Areas to Review</p>
              {areasToReview.length ? (
                <ul className="space-y-2">
                  {areasToReview.map((s) => (
                    <li key={s} className="flex items-start gap-2 text-[#101828] text-sm">
                      <span className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#475467]">Nothing flagged from this short check. A deeper review is still recommended.</p>
              )}
            </div>
          </div>

          <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-5 mb-8">
            <p className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold mb-3">Recommended Next Steps</p>
            <ul className="space-y-2 text-sm text-[#101828]">
              {(nextSteps.length ? nextSteps : ['Have a conversation about your current environment and priorities.']).map((s) => (
                <li key={s} className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/contact" className="w-full sm:w-auto flex-1">
              <Button
                data-testid="results-cta-consultation"
                className="w-full py-5 bg-[#06101F] hover:bg-[#0B1B35] text-white rounded-full font-semibold"
              >
                Request a Security Consultation
              </Button>
            </Link>
            <Button
              onClick={reset}
              variant="outline"
              className="w-full sm:w-auto py-5 bg-white border-slate-300 text-[#101828] hover:bg-slate-50 rounded-full font-medium"
            >
              Retake the Check
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Lead capture step
  if (showCapture) {
    return (
      <div className="max-w-xl mx-auto" data-testid="health-check-capture">
        <div className="relative bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-sm">
          <div className="text-center mb-6">
            <div className="inline-block p-3 bg-blue-50 border border-blue-100 rounded-full mb-4">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="font-editorial text-xl sm:text-2xl font-semibold text-[#06101F] mb-2">Get Your Security Snapshot</h3>
            <p className="text-[#475467] text-sm">
              Enter your details and we&apos;ll email you a short snapshot with practical next steps.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <Input type="text" placeholder="Full Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="capture-name" className={inputClass} />
            <Input type="text" placeholder="Company Name *" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} required data-testid="capture-company" className={inputClass} />
            <Input type="email" placeholder="Business Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="capture-email" className={inputClass} />
            <select
              value={form.companySize}
              onChange={(e) => setForm({ ...form, companySize: e.target.value })}
              required
              data-testid="capture-size"
              className="w-full h-12 bg-white border border-slate-300 text-[#101828] rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            >
              <option value="">Company Size *</option>
              <option value="1-10">1–10 employees</option>
              <option value="11-50">11–50 employees</option>
              <option value="51-200">51–200 employees</option>
              <option value="201-500">201–500 employees</option>
              <option value="500+">500+ employees</option>
            </select>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="capture-submit"
              className="w-full py-5 bg-[#06101F] hover:bg-[#0B1B35] text-white rounded-full font-semibold"
            >
              {submitting ? 'Sending…' : (<>Send My Snapshot <ArrowRight className="ml-2 w-4 h-4" /></>)}
            </Button>
            <p className="text-xs text-slate-500 text-center mt-2">
              We respect your privacy. Your details are only used to send your snapshot and follow up.
            </p>
          </form>
        </div>
      </div>
    );
  }

  // Question steps
  const q = questions[step];
  return (
    <div className="max-w-3xl mx-auto" data-testid="health-check-quiz">
      <div className="mb-5 flex items-center justify-between text-xs text-[#475467]">
        <span className="font-medium">Question {step + 1} of {questions.length}</span>
        <span className="text-blue-700 font-semibold">{Math.round(((step + 1) / questions.length) * 100)}% complete</span>
      </div>
      <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden mb-6">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all"
          style={{ width: `${((step + 1) / questions.length) * 100}%` }}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-9 shadow-sm">
        <p className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold mb-3">{q.area}</p>
        <h3 className="font-editorial text-lg sm:text-2xl font-semibold text-[#06101F] leading-relaxed mb-6">
          {q.text}
        </h3>
        <div className="space-y-3">
          {q.options.map((o) => (
            <button
              key={o.value}
              onClick={() => handleAnswer(q.id, o.value)}
              data-testid={`hc-q${step + 1}-${o.value}`}
              className={`w-full p-4 sm:p-5 text-left rounded-xl border transition-all ${
                answers[q.id] === o.value
                  ? 'border-blue-600 bg-blue-50 text-[#06101F]'
                  : 'border-slate-200 bg-white text-[#101828] hover:border-blue-500/50 hover:bg-slate-50'
              }`}
            >
              <span className="text-sm sm:text-base font-medium">{o.label}</span>
            </button>
          ))}
        </div>
      </div>

      {step > 0 && (
        <div className="text-center mt-4">
          <button onClick={() => setStep(step - 1)} className="text-blue-700 hover:text-blue-900 text-sm font-medium">
            &larr; Previous question
          </button>
        </div>
      )}
    </div>
  );
};

export default SecurityHealthCheck;
